import {
  ChangeDetectionStrategy,
  Component,
  effect,
  input,
  output,
  signal,
  untracked,
} from '@angular/core';
import { form, FormField as SignalFormField } from '@angular/forms/signals';
import { SignalDropdown } from '@vallift/ngl';
import type { FormField, SpacerSize } from '../../../../models/built-form';

interface SelectOption<T> {
  label: string;
  value: T;
}

interface SpacerConfigModel {
  spacerSize: SpacerSize;
}

@Component({
  selector: 'app-spacer-config-view',
  standalone: true,
  imports: [SignalFormField, SignalDropdown],
  templateUrl: './spacer-config-view.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SpacerConfigViewComponent {
  readonly field = input.required<FormField>();
  readonly fieldChange = output<FormField>();

  readonly spacerSizeOptions: SelectOption<SpacerSize>[] = [
    { label: 'Small', value: 'sm' },
    { label: 'Medium', value: 'md' },
    { label: 'Large', value: 'lg' },
  ];

  readonly model = signal<SpacerConfigModel>({ spacerSize: 'md' });
  readonly configForm = form(this.model);

  private lastFieldId: string | null = null;
  private suppressEmit = false;

  constructor() {
    effect(() => {
      const next = this.field();
      if (next.id === this.lastFieldId) {
        return;
      }
      this.lastFieldId = next.id;
      this.suppressEmit = true;
      this.model.set({ spacerSize: next.spacerSize ?? 'md' });
    });

    effect(() => {
      const draft = this.model();
      if (this.suppressEmit) {
        this.suppressEmit = false;
        return;
      }
      const field = untracked(() => this.field());
      if (!field || field.id !== this.lastFieldId) {
        return;
      }
      this.fieldChange.emit({
        ...field,
        spacerSize: draft.spacerSize,
        placeholder: undefined,
        helpText: undefined,
        required: undefined,
        validation: undefined,
        options: undefined,
        headingLevel: undefined,
      });
    });
  }
}
